/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Modal,
  Popover,
  styled,
  SwipeableDrawer,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  createSvgIcon,
  Link as MuiLink,
} from "@mui/material";

import { AppTitle } from "./AppTitle";

import { MobileMenu } from "./Menu";

import { ProfileButton, UserAccount, VerticalDivider } from "./UserAccount";
import { CURRENCY_NAME } from "../../utils/constants";
import { useAxios } from "../../services/http.service";
import ApiRoutes from "../../config/api.routes";
import { consoleError, sizing, toReadable } from "../../utils/methods";
import { Text } from "../Typography";
import { useTheme } from "@mui/material/styles";
import { Row } from "../FlexComponents";
import { CircularIcon, DialogInfo, IconContainer, InfoBody, InfoButton, Wrapper } from "../../pages/Profile/Portal";
import InfoIconOutlined from "@mui/icons-material/InfoOutlined";
import useAuthorization from "../../contexts/useAuthorization";
import { AppRoutes } from "../../config";
import { SearchNoResultsDialog } from "./SearchNoResultsDialog";
import { useLocation, useNavigate, Link, generatePath } from "react-router-dom";
import { isAddress } from "web3-validator";
import PopUpGraph from "../LineChart";
import { WalletOutlined, Close as CloseIcon } from "@mui/icons-material";
import { BackButton, SearchButton } from "../../pages/EcoBank/components";
import { getReadOnlyContract } from "../../services/contract.service";
import { ClickableContainer } from "../../utils/table-schemas";
import { NotificationBell } from "../Notifications";
interface HeaderProps {
  toggleNavigation?: () => void;
}

type SearchResponse = {
  type: "admin" | "user" | "trust" | "land" | "none";
  search: string | number;
};

const SearchField = styled("div")`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

// Search reveal easing (project entrance curve). will-change is armed only while the reveal runs.
const SEARCH_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const markSearchAnimating = (el: HTMLElement) => {
  const root = el.closest<HTMLElement>("[data-search-root]");
  if (!root) return;
  root.dataset.animating = "true";
  window.setTimeout(() => {
    delete root.dataset.animating;
  }, 700);
};

export const Header = (_props: HeaderProps) => {
  const axios = useAxios();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setIndicatorsLoading] = useState(true);
  const [mintRate, setMintRate] = useState<any | null>(null);
  const [mintRateTrend, setMintRateTrend] = useState<"up" | "down" | "same">("same");
  const [mintRateChange, setMintRateChange] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  // Search state
  const [searchQuery, setSearch] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const [acceptOrg, setAcceptOrg] = useState(false);
  const location = useLocation();
  const { loggedIn, user, isAdmin, isGlobalTrustAdmin, isGuardian, isPioneer, isTrust } = useAuthorization();

  // Mobile wallet balance popover state
  const [walletAnchorEl, setWalletAnchorEl] = useState<null | HTMLElement>(null);
  const [ercBalance, setErcBalance] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const walletPopoverOpen = Boolean(walletAnchorEl);

  const handleWalletClick = (event: React.MouseEvent<HTMLElement>) => {
    setWalletAnchorEl(event.currentTarget);
    if (!ercBalance && !balanceLoading) {
      fetchWalletBalance();
    }
  };

  const handleWalletPopoverClose = () => {
    setWalletAnchorEl(null);
  };

  const fetchWalletBalance = async () => {
    setBalanceLoading(true);
    try {
      const ercContract = getReadOnlyContract("erc20");

      if (ercContract && user && user.address) {
        const balance = await ercContract.balanceOf(user.address);
        setErcBalance(String(toReadable(balance, true)));
      }
    } catch (error) {
      console.error("Error fetching wallet balance:", error);
      setErcBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleAccept = () => {
    navigate(AppRoutes.BecomeTrust.path);
    setAcceptOrg(false);
  };
  // Search logic. Wallets open their profile; lands open in Explore's popup view
  // (best match's card + a thumbnail strip of the matches) — `?id=` for an exact
  // ID hit, `?search=` for a lands-index hit. Nothing found (or the lookup failed)
  // never navigates: SearchNoResultsDialog says so and the user stays put.
  const getSearchParameters = (query: string) => {
    if (isAddress(query)) {
      return `?address=${query}`;
    } else if (!isNaN(Number(query))) {
      if (Number(query) > 1000) {
        return false;
      }
      return `?id=${query}`;
    } else {
      return false;
    }
  };

  const getResultUrl = ({ type, search }: SearchResponse) => {
    switch (type) {
      case "admin":
      case "user":
      case "trust":
        return generatePath(AppRoutes.UserWallet.path, { address: search });
      case "land":
        return `${AppRoutes.Explore.path}?id=${search}`;
      default:
        return false;
    }
  };

  // Where the query leads, or null when nothing matches. Throws if the lands
  // search fails (the caller treats that as nothing found).
  const resolveSearch = async (query: string): Promise<string | null> => {
    const params = getSearchParameters(query);
    if (params) {
      try {
        const result = await axios.get(ApiRoutes.ecoBank.search + params).then((res) => res.data as SearchResponse);
        const url = getResultUrl(result);
        if (url) {
          return url;
        }
      } catch (e) {
        // The exact lookup is only a shortcut — the lands search below still runs.
        consoleError("Failed to perform search request", { e });
      }
    }
    const hits: unknown[] = await axios.get(ApiRoutes.ecoBank.lands.search(query)).then((res) => res.data);
    return hits.length > 0 ? `${AppRoutes.Explore.path}?search=${encodeURIComponent(query)}` : null;
  };

  // The searched term while the no-results dialog is open.
  const [noResultsTerm, setNoResultsTerm] = useState<string | null>(null);

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query || searchLoading) {
      return;
    }
    setSearchLoading(true);
    try {
      const url = await resolveSearch(query);
      if (url) {
        // headerSearch: Explore narrows its thumbnail strip to the matches.
        navigate(url, { state: { headerSearch: true } });
      } else {
        setNoResultsTerm(query);
      }
    } catch (e) {
      consoleError("Failed to search lands", { e });
      setNoResultsTerm(query);
    } finally {
      setSearchLoading(false);
    }
  };

  // Back to the field with the term selected, ready to retype.
  const closeNoResults = () => {
    setNoResultsTerm(null);
    window.setTimeout(() => {
      const input = document.getElementById("header-search-items") as HTMLInputElement | null;
      input?.focus();
      input?.select();
    }, 0);
  };

  const isBecomingTrust = location.pathname.includes(AppRoutes.BecomeTrust.path) ? true : false;
  const account = location.pathname.includes(AppRoutes.Profile.path) ? true : false;
  // const isWalletActive = useMatch({ path: AppRoutes.Profile.subRoutes.Dashboard.path, end: false });
  // const isVerificationActive = useMatch({ path: AppRoutes.Profile.subRoutes.Verification.path, end: false });
  // const isPortfolioActive = useMatch({ path: AppRoutes.Profile.subRoutes.Collection.path, end: false });
  // const isAccountActive = useMatch({ path: AppRoutes.Profile.subRoutes.Identification.path, end: false });
  const handleReject = async () => {
    setIsLoading(true);
    try {
      if (user?.trusteeStatus === "invited") {
        const response = await axios.post(ApiRoutes.user.declineTrustee).then((res) => res.data);
        if (response) {
          setAcceptOrg(false);
          window.location.reload();
        }
      }
      if (user?.trustStatus === "invited") {
        const response = await axios.post(ApiRoutes.user.declineTrust).then((res) => res.data);
        if (response) {
          setAcceptOrg(false);
          window.location.reload();
        }
      }
    } catch (e: any) {
      console.log({ e });
      setError(e?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      window.location.href = AppRoutes.Home.path;
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // navigate(AppRoutes.EcoBank.path);
  };
  const handleClose = () => setOpen(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIndicatorsLoading(true);
      const [result, statsResult] = await Promise.all([
        axios.get(ApiRoutes.ecoBank.indicators.root).then((res) => res.data),
        axios
          .get(ApiRoutes.ecoBank.stats(2))
          .then((res) => res.data)
          .catch(() => null),
      ]);
      const trees = result.tokens.reduce((acc: any, cur: any) => acc + toReadable(cur, cur !== "0", false), 0);
      const prices = result.prices.reduce((acc: any, cur: any) => acc + toReadable(cur, true, false), 0);
      const rate = toReadable(prices / trees || 0, false, true, 4, CURRENCY_NAME);
      setMintRate(rate);

      if (Array.isArray(statsResult) && statsResult.length >= 2) {
        const sorted = [...statsResult].sort(
          (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const current = Number(sorted[0].price);
        const previous = Number(sorted[1].price);
        if (current > previous) setMintRateTrend("up");
        else if (current < previous) setMintRateTrend("down");
        else setMintRateTrend("same");
        if (previous !== 0) setMintRateChange(((current - previous) / previous) * 100);
      }
    } catch (e: any) {
      console.log({ e });
    } finally {
      setIndicatorsLoading(false);
    }
  };

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setMobileMoreAnchorEl(event.currentTarget);

  const handleMobileMenuClose = () => setMobileMoreAnchorEl(null);

  const white = location.pathname === "/help" || location.pathname === "/help/";

  return (
    <>
      <AppBar
        id="eco-header"
        component={"div"}
        position="relative"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CustomToolBar
          variant="dense"
          sx={{
            flexWrap: "wrap",
            paddingX: sizing(6),
            padding: { xs: "0 18px", md: "0 24px" },
            border: "none",
            // borderColor: ({ palette }) => palette.divider,
            // backgroundColor: ({ palette }) => palette.back.orange,
            backgroundColor: "black",
          }}
        >
          <Row sx={{ alignItems: "center", gap: "6px" }}>
            <Text medium sx={{ cursor: "default", color: "white" }} numeric>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
                onClick={() => handleOpen()}
              >
                <ClickableContainer
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f5f5f52d",
                    },
                    "&:active": {
                      backgroundColor: "#f5f5f52d",
                    },
                  }}
                >
                  <Box component="span" style={{ textDecoration: "none" }}>
                    {loading ? "Loading..." : mintRate}
                  </Box>

                  {!loading && mintRate && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        fontWeight: 400,
                        color: mintRateTrend === "down" ? "#f44336" : "#4caf50",
                      }}
                    >
                      <Box component="span" sx={{ scale: 1, lineHeight: 1, display: "inline-block" }}>
                        {mintRateTrend === "down" ? "▼" : "▲"}
                      </Box>
                      <Box component="span" sx={{ ml: "2px" }}>
                        {mintRateChange !== null ? Math.abs(mintRateChange).toFixed(2) : "0.00"}%
                      </Box>
                    </Box>
                  )}
                </ClickableContainer>
              </Box>
            </Text>
          </Row>

          {/* Right-aligned account section container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
              gap: 8,
            }}
          >
            {/* Wallet icon only for mobile */}
            {loggedIn && isMobile && (
              <UserAccountWrap sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "white",
                    cursor: "pointer",
                  }}
                  onClick={handleWalletClick}
                >
                  <WalletOutlined sx={{ width: "24px", height: "24px" }} />
                </Box>
                <Popover
                  open={walletPopoverOpen}
                  anchorEl={walletAnchorEl}
                  onClose={handleWalletPopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  sx={{
                    "& .MuiPaper-root": {
                      backgroundColor: "#1a1a1a",
                      borderRadius: "8px",
                      marginTop: "8px",
                    },
                  }}
                >
                  <MuiLink
                    href="https://portfolio.metamask.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                      color: "white",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    <Text
                      numeric
                      medium
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "white",
                      }}
                    >
                      <WalletOutlined sx={{ mr: 3, display: "flex", alignSelf: "center" }} />
                      {balanceLoading ? "Loading..." : ercBalance ?? "Unable to fetch"}
                      <Text
                        sx={{
                          ml: 3,
                          display: "flex",
                          alignItems: "center",
                        }}
                        medium
                      >
                        USD
                      </Text>
                    </Text>
                  </MuiLink>
                </Popover>
                <VerticalDivider />
              </UserAccountWrap>
            )}

            {/* Account section */}
            {loggedIn && isMobile && (
              <UserAccountWrap>
                <ProfileButton
                  sx={{
                    color: "black",
                    border: { xs: "1px solid", md: "1px solid" },
                    borderColor: "currentColor",
                  }}
                  size="medium"
                >
                  <AccountLink to={AppRoutes.Profile.subRoutes.Dashboard.path}>
                    {isGuardian && <GuardianIcon sx={{ width: "18px", height: "18px" }} />}
                    {isPioneer && <PioneerIcon sx={{ width: "18px", height: "18px" }} />}
                    {isGlobalTrustAdmin && <GlobalTrustAdminIcon sx={{ width: "18px", height: "18px" }} />}
                    {isAdmin && !isGlobalTrustAdmin && <TrustAdminIcon sx={{ width: "18px", height: "18px" }} />}
                    {isTrust && <TrusteeIcon sx={{ width: "18px", height: "18px" }} />}
                    {!isGuardian && !isPioneer && !isAdmin && !isTrust && (
                      <PatronIcon sx={{ width: "18px", height: "18px" }} />
                    )}
                  </AccountLink>
                </ProfileButton>
                <VerticalDivider />
              </UserAccountWrap>
            )}

            {/* login logout buttons */}
            <UserAccountWrap>
              <UserAccount />
            </UserAccountWrap>
          </Box>
        </CustomToolBar>

        <CustomToolBar
          variant="dense"
          sx={{
            maxWidth: 1560,
            margin: "0 auto",
            paddingX: { xs: "18px", md: "24px" },
            position: "relative",
            // The collapsed search panel below is squeezed into its 40px root and parked 24px
            // right of it, so its hidden contents spill ~40px past the viewport. body's
            // overflow-x: clip doesn't help on mobile: it propagates to the viewport, and iOS/
            // Chrome still widen the layout viewport to fit, so pinch-out reveals a white strip
            // down the right of every page. Clipping here keeps that overflow out of the page.
            // x only — clip (unlike hidden) leaves overflow-y visible and makes no scroller.
            overflowX: "clip",
            border: "none",
            width: "100%",
            borderRadius: "0px 0px 20px 20px",
            // user?.pendingReassignment?.hasPendingReassignment === true
            //   ? "0px 0px 20px 20px"
            //   : isWalletActive
            //   ? { md: "0px", xs: "0px 0px 20px 0px" }
            //   : isVerificationActive
            //   ? { md: "0px", xs: "0px 0px 20px 0px" }
            //   : isPortfolioActive
            //   ? { md: "0px", xs: "0px 0px 20px 20px" }
            //   : isAccountActive
            //   ? { md: "0px", xs: "0px 0px 0px 20px" }
            //   : "0px 0px 20px 20px",
            backgroundColor: white ? "#e5e5e5" : "#fff",
          }}
        >
          {/* Left: hamburger + logo — hidden when search is expanded on mobile only */}
          <Box
            sx={{
              display: { xs: isSearchExpanded ? "none" : "flex", md: "flex" },
              alignItems: "center",
              gap: "21px",
              minWidth: 0,
            }}
          >
            <HamburgerButton onClick={handleMobileMenuOpen} size="small">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 10H17.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M2.5 5H17.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M2.5 15H17.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </svg>
            </HamburgerButton>
            <AppTitle />
          </Box>

          {/* Center: inline nav links — desktop only */}
          {/* <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <Navigation />
          </Box> */}

          {/* Right-aligned header actions: search + notification bell */}
          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: { xs: "8px", md: "12px" },
              minWidth: 0,
              // When search is expanded on mobile it takes the full row, so the
              // bell would crowd it out — collapse the wrapper to the search width.
              flex: isSearchExpanded ? { xs: 1, md: "0 0 auto" } : "0 0 auto",
            }}
          >
            {/* Search component */}
            <Box
              data-search-root
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                position: "relative",
                height: "40px",
                // Layout width flips once; the visible reveal below is transform/opacity only.
                width: isSearchExpanded ? { xs: "100%", md: "400px" } : "40px",
                "&[data-animating='true'] .search-anim": { willChange: "transform, opacity" },
              }}
            >
              {/* Collapsed search icon — scales/fades out when expanded */}
              <SearchButton
                onClick={(e) => {
                  markSearchAnimating(e.currentTarget);
                  setIsSearchExpanded(true);
                }}
                aria-label="expand search"
                tabIndex={isSearchExpanded ? -1 : 0}
                className="search-anim"
                sx={(theme) => ({
                  position: "absolute",
                  right: 0,
                  width: "40px",
                  height: "40px",
                  minWidth: "40px",
                  border: `1px solid ${theme.palette.border.default}`,
                  transformOrigin: "center",
                  transform: isSearchExpanded ? "scale3d(0.8, 0.8, 1)" : "scale3d(1, 1, 1)",
                  opacity: isSearchExpanded ? 0 : 1,
                  visibility: isSearchExpanded ? "hidden" : "visible",
                  pointerEvents: isSearchExpanded ? "none" : "auto",
                  transition: isSearchExpanded
                    ? `transform 160ms ${SEARCH_EASE}, opacity 120ms ${SEARCH_EASE}, visibility 0s linear 160ms`
                    : `transform 350ms ${SEARCH_EASE} 140ms, opacity 250ms ${SEARCH_EASE} 140ms, visibility 0s linear 140ms`,
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.06)" },
                  "& svg": { width: "16px", height: "16px", color: theme.palette.text.primary },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                })}
              >
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13 13L10.1 10.1M11.6667 6.33333C11.6667 9.27885 9.27885 11.6667 6.33333 11.6667C3.38781 11.6667 1 9.27885 1 6.33333C1 3.38781 3.38781 1 6.33333 1C9.27885 1 11.6667 3.38781 11.6667 6.33333Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SearchButton>

              {/* Expanded panel — laid out at full size, slides/fades in from the right via transform */}
              <Box
                className="search-anim"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transformOrigin: "right center",
                  transform: isSearchExpanded
                    ? "translate3d(0, 0, 0) scale3d(1, 1, 1)"
                    : "translate3d(24px, 0, 0) scale3d(0.94, 0.94, 1)",
                  opacity: isSearchExpanded ? 1 : 0,
                  visibility: isSearchExpanded ? "visible" : "hidden",
                  pointerEvents: isSearchExpanded ? "auto" : "none",
                  transition: isSearchExpanded
                    ? `transform 400ms ${SEARCH_EASE}, opacity 300ms ${SEARCH_EASE}, visibility 0s linear 0s`
                    : `transform 220ms ${SEARCH_EASE}, opacity 160ms ${SEARCH_EASE}, visibility 0s linear 220ms`,
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
              >
                {/* Pill — soft border + inner shadow; focus ring is an opacity-only pseudo-element */}
                <Box
                  sx={(theme) => ({
                    position: "relative",
                    flex: 1,
                    minWidth: 0,
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "0 5px 0 14px",
                    borderRadius: "20px",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.border.default}`,
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
                    transition: "border-color 0.2s ease",
                    "&:hover": { borderColor: theme.palette.text.disabled },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: "-1px",
                      borderRadius: "inherit",
                      border: `1.5px solid ${theme.palette.text.primary}`,
                      boxShadow: "0 0 0 4px rgba(0,0,0,0.08)",
                      opacity: 0,
                      pointerEvents: "none",
                      transition: "opacity 0.2s ease",
                    },
                    "&:focus-within::after": { opacity: 1 },
                    ...theme.applyStyles("dark", {
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.4)",
                      "&::after": { boxShadow: "0 0 0 4px rgba(255,255,255,0.12)" },
                    }),
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                      "&::after": { transition: "none" },
                    },
                  })}
                >
                  {/* Leading search icon */}
                  <Box
                    className="search-anim"
                    aria-hidden
                    sx={(theme) => ({
                      display: "flex",
                      flexShrink: 0,
                      color: theme.palette.text.disabled,
                      transform: isSearchExpanded ? "translate3d(0, 0, 0)" : "translate3d(8px, 0, 0)",
                      opacity: isSearchExpanded ? 1 : 0,
                      transition: isSearchExpanded
                        ? `transform 400ms ${SEARCH_EASE} 60ms, opacity 300ms ${SEARCH_EASE} 60ms`
                        : "none",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    })}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M13 13L10.1 10.1M11.6667 6.33333C11.6667 9.27885 9.27885 11.6667 6.33333 11.6667C3.38781 11.6667 1 9.27885 1 6.33333C1 3.38781 3.38781 1 6.33333 1C9.27885 1 11.6667 3.38781 11.6667 6.33333Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                  <SearchField>
                    <TextField
                      variant="outlined"
                      inputMode="search"
                      type="search"
                      size="small"
                      fullWidth
                      inputProps={{
                        id: "header-search-items",
                        enterKeyHint: "search",
                      }}
                      inputRef={(el: HTMLInputElement | null) => {
                        if (el && isSearchExpanded && document.activeElement !== el) {
                          // Focus after the expansion animation completes
                          setTimeout(() => el.focus(), 350);
                        }
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <SearchButton
                              disabled={!searchQuery || searchLoading}
                              onClick={() => void handleSearch()}
                              aria-label="search"
                              tabIndex={isSearchExpanded ? 0 : -1}
                              sx={(theme) => ({
                                position: "relative",
                                width: "30px",
                                height: "30px",
                                minWidth: "30px",
                                // Popup CTA states: black → #333 hover, translucent when disabled, #333 while busy
                                backgroundColor: theme.palette.text.primary,
                                "&:hover": { backgroundColor: "#333" },
                                "&.Mui-disabled": {
                                  backgroundColor: searchLoading ? "#333" : "rgba(0, 0, 0, 0.4)",
                                  opacity: 1,
                                },
                                "& svg": { width: "14px", height: "14px", color: theme.palette.text.secondary },
                                ...theme.applyStyles("dark", {
                                  "&:hover": { backgroundColor: "#ddd" },
                                  "&.Mui-disabled": {
                                    backgroundColor: searchLoading ? "#ddd" : "rgba(255, 255, 255, 0.4)",
                                  },
                                }),
                              })}
                            >
                              {/* Arrow ↔ spinner cross-fade (opacity + scale only) */}
                              <Box
                                component="span"
                                sx={{
                                  display: "flex",
                                  transform: searchLoading ? "scale3d(0.6, 0.6, 1)" : "scale3d(1, 1, 1)",
                                  opacity: searchLoading ? 0 : 1,
                                  transition: `transform 200ms ${SEARCH_EASE}, opacity 150ms ease`,
                                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M13 13L10.1 10.1M11.6667 6.33333C11.6667 9.27885 9.27885 11.6667 6.33333 11.6667C3.38781 11.6667 1 9.27885 1 6.33333C1 3.38781 3.38781 1 6.33333 1C9.27885 1 11.6667 3.38781 11.6667 6.33333Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </Box>
                              <CircularProgress
                                size={16}
                                thickness={5}
                                sx={(theme) => ({
                                  position: "absolute",
                                  color: theme.palette.text.secondary,
                                  opacity: searchLoading ? 1 : 0,
                                  transition: "opacity 150ms ease",
                                  ...theme.applyStyles("dark", { color: "#000" }),
                                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                                })}
                              />
                            </SearchButton>
                          ),
                        },
                      }}
                      value={searchQuery}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") {
                          (e.target as HTMLInputElement).blur();
                          void handleSearch();
                        }
                      }}
                      id="header-search-items"
                      placeholder="Wallet address / EFT ID / Land name / Land location"
                      sx={(theme) => ({
                        "& .MuiOutlinedInput-root": {
                          color: theme.palette.text.primary,
                          backgroundColor: "transparent",
                          fontSize: "13px",
                          width: "100%",
                          paddingRight: 0,
                          "& fieldset, &:hover fieldset, &.Mui-focused fieldset": { border: "none" },
                          "& input": { padding: "8px 4px 8px 0", textOverflow: "ellipsis" },
                          "& input::-webkit-search-cancel-button": { display: "none" },
                          "& input::placeholder": { color: theme.palette.text.disabled, opacity: 1 },
                        },
                      })}
                    />
                  </SearchField>
                </Box>
                {/* Close — circular ghost button; icon rotates/scales in with a stagger */}
                <IconButton
                  onClick={(e) => {
                    markSearchAnimating(e.currentTarget);
                    setIsSearchExpanded(false);
                    setSearch("");
                    if (new URLSearchParams(location.search).get("search")) {
                      navigate(location.pathname);
                    }
                  }}
                  aria-label="close search"
                  tabIndex={isSearchExpanded ? 0 : -1}
                  sx={(theme) => ({
                    flexShrink: 0,
                    width: "32px",
                    height: "32px",
                    padding: 0,
                    border: `1px solid ${theme.palette.border.default}`,
                    // Theme paints buttons solid; declare bg + hover explicitly (ghost)
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.06)" },
                  })}
                >
                  <CloseIcon
                    className="search-anim"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                      width: "16px",
                      height: "16px",
                      transform: isSearchExpanded
                        ? "rotate(0deg) scale3d(1, 1, 1)"
                        : "rotate(-90deg) scale3d(0.5, 0.5, 1)",
                      opacity: isSearchExpanded ? 1 : 0,
                      transition: isSearchExpanded
                        ? `transform 400ms ${SEARCH_EASE} 140ms, opacity 300ms ${SEARCH_EASE} 140ms`
                        : "none",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    })}
                  />
                </IconButton>
              </Box>
            </Box>

            {/* Notification bell — renders only for logged-in users, hidden while
                the mobile search is expanded to keep the row from overflowing. */}
            {!(isMobile && isSearchExpanded) && <NotificationBell />}
          </Box>
        </CustomToolBar>
        {user?.pendingReassignment?.hasPendingReassignment === true && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "18px", md: "24px" },
              paddingY: { xs: "18px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "start",
              cursor: "pointer",
              borderRadius: { xs: "12px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "auto", sm: "auto" },
              minHeight: { xs: "auto", sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "2px", sm: "20px" },
              }}
            >
              Trust Admin has resassigned {user?.pendingReassignment?.reassignments.length} EFT(s) to you. Please visit
              Portal to accept/reject the reassignment. EFT ID(s) : #
              {user?.pendingReassignment?.reassignments.map((item) => item.etNftId).join(", #")}
            </Typography>
            <BackButton
              onClick={() => navigate(`${AppRoutes.Profile.subRoutes.Verification.path}?uploads=true`)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              View
            </BackButton>
          </CustomToolBar>
        )}
        {(user?.trusteeStatus === "invited" || user?.trustStatus === "invited") && !isBecomingTrust && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "0", sm: "0" },
              paddingY: { xs: "4px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: { xs: "12px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "auto", sm: "78px" },
              minHeight: { xs: "auto", sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "2px", sm: "20px" },
              }}
            >
              You have been invited to {user?.trustStatus === "invited" ? "create" : "join"} a Trust Organization. Click
              here for more information
            </Typography>
            <BackButton
              onClick={() => setAcceptOrg(true)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              {user?.trustStatus === "invited" ? "Create" : "Join"}
            </BackButton>
          </CustomToolBar>
        )}
        {user?.trustAdminStatus === "invited" && !isBecomingTrust && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "0", sm: "0" },
              paddingY: { xs: "4px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: { xs: "12px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "auto", sm: "60px" },
              minHeight: { xs: "auto", sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "2px", sm: "20px" },
              }}
            >
              You have been invited to become an EcoFutures Trust Admin. Complete your account verification to continue.
            </Typography>
            <BackButton
              onClick={() => navigate(AppRoutes.BecomeTrust.path)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              Continue
            </BackButton>
          </CustomToolBar>
        )}
        {(user?.trusteeStatus === "declined" || user?.trustStatus === "declined") && !isBecomingTrust && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "0", sm: "0" },
              paddingY: { xs: "4px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: { xs: "12px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "auto", sm: "78px" },
              minHeight: { xs: "auto", sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "2px", sm: "20px" },
              }}
            >
              Your previous Trust Organization application was declined. You can update your information and resubmit to{" "}
              {user?.trustStatus === "declined" ? "create" : "join"} a Trust Organization.
            </Typography>
            <BackButton
              onClick={() => navigate(AppRoutes.BecomeTrust.path)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              Resubmit
            </BackButton>
          </CustomToolBar>
        )}
        {user?.trustAdminStatus === "declined" && !isBecomingTrust && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "0", sm: "0" },
              paddingY: { xs: "4px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: { xs: "12px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "auto", sm: "60px" },
              minHeight: { xs: "auto", sm: "auto" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "2px", sm: "20px" },
              }}
            >
              Your previous EcoFutures Trust Admin application was declined. You can update your information and
              resubmit to continue.
            </Typography>
            <BackButton
              onClick={() => navigate(AppRoutes.BecomeTrust.path)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              Resubmit
            </BackButton>
          </CustomToolBar>
        )}
        {user?.emailVerified === false && !account && (
          <CustomToolBar
            variant="dense"
            sx={{
              maxWidth: 1560,
              margin: "0 auto",
              paddingX: { xs: "0", sm: "0" },
              paddingY: { xs: "4px", sm: "6px" },
              width: "100%",
              backgroundColor: ({ palette }) => palette.back.searchComponent,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: { xs: "20px", sm: "23px" },
              border: "none",
              maxHeight: { xs: "fit-content", sm: "60px" },
              minHeight: { xs: "60px", sm: "60px" },
            }}
          >
            <Typography
              variant="h6"
              fontFamily={"sans-serif"}
              sx={{
                color: "black",
                cursor: "pointer",
                fontSize: { xs: "14px", sm: "16px", md: "1.25rem" },
                lineHeight: { xs: 1.3, sm: 1.6 },
                padding: { xs: "0px", sm: "20px" },
              }}
            >
              Your email is not verified! Please verify your email to access all features of EcoFutures.
            </Typography>
            <BackButton
              onClick={() => navigate(AppRoutes.Profile.subRoutes.Identification.path)}
              sx={{
                marginRight: "23px",
                borderRadius: { xs: "12px", sm: "23px" },
                "&:hover": {
                  border: "none",
                },
              }}
            >
              Verify
            </BackButton>
          </CustomToolBar>
        )}
      </AppBar>
      <MobileMenu
        isMenuOpen={Boolean(mobileMoreAnchorEl)}
        handleMenuClose={handleMobileMenuClose}
        anchorEl={mobileMoreAnchorEl}
      />

      {!isMobile && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ display: { md: "flex", sm: "none" }, alignItems: "center", justifyContent: "center" }}
        >
          <PopUpGraph onClose={handleClose} type={"price"} />
        </Modal>
      )}

      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          sx={{
            display: { xs: "block", sm: "none" },
            height: "100%",
            width: "100%",
            "& .MuiPaper-root": {
              backgroundColor: "transparent",
            },
          }}
        >
          <PopUpGraph onClose={handleClose} type={"price"} />
        </SwipeableDrawer>
      )}

      <DialogInfo open={acceptOrg} onClose={() => setAcceptOrg(false)} title={""}>
        <Wrapper
          sx={{
            p: 12,
            backgroundColor: "white",
            borderColor: "white",
          }}
        >
          <IconContainer>
            <CircularIcon>
              <InfoIconOutlined fontSize="large" color="primary" />
            </CircularIcon>
          </IconContainer>
          <InfoBody sx={{ p: 0, mt: 16 }}>
            <br />
            <p style={{ fontWeight: 600, color: "black", alignSelf: "center", fontSize: "26px" }}>Accept Invitation?</p>
            <br />
            <p style={{ lineHeight: "1.5", textAlign: "center" }}>
              You have been invited to {user?.trustStatus === "invited" ? "create" : "join"} a Trust Organization.{" "}
              <br />
              {user?.trustStatus === "invited" ? (
                ""
              ) : (
                <>
                  Organization Name: {user?.organization?.name} <br />
                  Country: {user?.organization?.country} <br />
                </>
              )}
              {error?.length > 0 && <p style={{ color: "red" }}>{error}</p>}
            </p>
            {/* <p style={{ lineHeight: "1.5", textAlign: "center", color: "red" }} >
                {selectedUser?.orgVerified === true ? "" : "Organization is not Verified"}
            </p> */}
          </InfoBody>
          {/* <Typography fontSize="14px" margin="12px 0" style={{ color: palette.error.main }}>
                        {error}
                    </Typography> */}
          <Grid
            container
            direction={"row"}
            alignItems="center"
            justifyContent={"space-around"}
            sx={{
              borderBottom: "1px solid GrayText",
              pb: 8,
              width: "100%",
            }}
          >
            {isMobile ? (
              <>
                <InfoButton onClick={() => handleAccept()} color="primary">
                  Accept
                </InfoButton>
                <InfoButton
                  sx={{
                    backgroundColor: "#999999",
                    border: "none",
                  }}
                  onClick={() => handleReject()}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading" : "Reject"}
                </InfoButton>
              </>
            ) : (
              <>
                <InfoButton
                  sx={{
                    backgroundColor: "#999999",
                    border: "none",
                  }}
                  onClick={() => handleReject()}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading" : "Reject"}
                </InfoButton>
                <InfoButton onClick={() => handleAccept()} color="primary">
                  Accept
                </InfoButton>
              </>
            )}
          </Grid>
          <br />
          <p style={{ paddingTop: "24px" }}>
            <a
              href="/help?blog=blog-article-3"
              style={{ marginRight: "4px" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Here
            </a>
            is an in-depth article covering thie role and responsibilities of the Trustee.
          </p>
        </Wrapper>
      </DialogInfo>

      <SearchNoResultsDialog term={noResultsTerm} onClose={closeNoResults} />
    </>
  );
};

export const CustomToolBar = styled(Toolbar)`
  max-height: 60px;
  min-height: 60px;
  padding: 0;
`;

const UserAccountWrap = styled("div")``;

// Account Icons
const GuardianIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
    <path d="M480-320q102-92 131-129.5t29-74.5q0-36-26-62t-62-26q-21 0-40.5 8.5T480-580q-12-15-31-23.5t-41-8.5q-36 0-62 26t-26 62q0 19 5 35t22 37.5q17 21.5 48.5 52.5t84.5 79Zm0 240q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Z" />
  </svg>,
  "Guardian"
);

const TrustAdminIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
    <path d="m368-336 112-84 110 84-42-136 112-88H524l-44-136-44 136H300l110 88-42 136ZM480-80q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Z" />
  </svg>,
  "Trust Admin"
);

const PatronIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
    <path d="m438-338 226-226-57-57-169 169-84-84-57 57 141 141Zm42 258q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Z" />
  </svg>,
  "Patron"
);

// Pioneer and Global Trust Admin are the Figma role glyphs (32 × 32 frame, see
// components/RoleIcons). The square viewBox is centred on the shield and sized so it fills the
// box the way the Material shields above do (5/6 of the height).
const PioneerIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="4.878 4.913 22.164 22.164" width="24px" fill="#000000">
    <path
      fillRule="evenodd"
      d="M15.96 25.23C13.8 24.7 12.0098 23.48 10.5898 21.56C9.16984 19.64 8.45996 17.51 8.45996 15.16V9.65002L15.96 6.76001L23.46 9.65002V15.16C23.46 17.5 22.7501 19.64 21.3301 21.56C19.9101 23.48 18.12 24.71 15.96 25.23ZM13.25 19.39H14.71V15.88H19.8301L18.1899 13.68L19.8301 11.49H13.25V19.39Z"
    />
  </svg>,
  "Pioneer"
);

const GlobalTrustAdminIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="5.566 5.541 20.868 20.868" width="24px" fill="#000000">
    <path d="M16 24.67C13.97 24.16 12.3 22.99 11 21.14C9.7 19.29 9.04004 17.27 9.04004 15.06V9.89001L16 7.28003L22.96 9.89001V15.06C22.96 17.26 22.31 19.29 21 21.14C19.69 22.99 18.03 24.17 16 24.67ZM16 23.32C17.54 22.81 18.8101 21.88 19.8101 20.53C20.8201 19.18 21.4101 17.66 21.5801 15.98H15.9902V8.70001L10.3403 10.81V15.07C10.3403 15.24 10.3401 15.39 10.3501 15.52C10.3601 15.65 10.3804 15.8001 10.4004 15.9901H15.9902V23.34L16 23.32Z" />
  </svg>,
  "Global Trust Admin"
);

const TrusteeIcon = createSvgIcon(
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000">
    <path d="M420-340h120v-100h100v-120H540v-100H420v100H320v120h100v100Zm60 260q-139-35-229.5-159.5T160-516v-244l320-120 320 120v244q0 152-90.5 276.5T480-80Z" />
  </svg>,
  "Trustee"
);

const AccountLink = styled(Link)`
  text-decoration: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const HamburgerButton = styled(IconButton)`
  color: #1a1a1a;
  padding: 6px;
  border-radius: 6px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
`;
